import React, { useEffect, useState } from 'react';

import mixpanel from 'mixpanel-browser';
import { NumberParam, StringParam, useQueryParam, useQueryParams } from 'next-query-params';
import { queryTypes, useQueryState } from 'next-usequerystate';
import { useRouter } from 'next/router';

import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Info from '@material-ui/icons/InfoOutlined';

import createLead from '../../../common/util/partnerApi';
import { apiClient } from '../../apollo';
import Layout from '../components/book/Layout';
import LeadForm from '../components/book/LeadForm';
import AddLocation from '../components/book/Location/AddLocation';
import LocationsInput from '../components/book/Location/LocationsInput';
import PrivacyModal from '../components/book/Location/PrivacyModal';
import AlertMessageBox from '../components/shared/AlertMessageBox';
import Message from '../components/ui-kit/Message';
import TCPA_FINE_PRINT from '../constants/tcpa';
import { useEligiblePackageQuery, useLocationsQuery } from '../hooks/api-v2';
import { usePartners } from '../hooks/cms-v2';
import useMarketing from '../hooks/useMarketing';
import { identify, track } from '../modules/Analytics';
import { LocationsParamV2 } from '../modules/CustomQueryParamFilters';
import CustomerInfoParam from '../modules/CustomQueryParamFilters/CustomerInfoParam';
import {
  LABOR_ONLY_FLOW_INDEX,
  LABOR_ONLY_MARKET_FLOW_INDEX,
  LEAD_FLOW_INDEX,
  LONG_DISTANCE_FLOW_INDEX,
  MLC_AVAILABILITY_FLOW_INDEX,
  MOVING_SERVICES_FLOW_INDEX,
} from '../modules/Flow';
import GoogleMapsScript from '../modules/GoogleMapsScript';
import withAuth from '../modules/WithAuth';
import MLCAPIService from '../utils/MLCAPIService';
import COPY from '../utils/copy';
import preserveMarketingParams from '../utils/preserveMarketingParams';

const LocationsIllustration = '/book/icons/locationsIllustration.svg';

const Location = () => {
  const [truck] = useQueryState('truck', queryTypes.integer);
  const [locations, setLocations] = useQueryState('locations', LocationsParamV2);
  const [flow, setFlow] = useQueryState('flow', queryTypes.integer);
  const [, setGroup] = useQueryState('group');

  const [params, setParams] = useQueryParams({
    clickid: StringParam,
    gclid: StringParam,
    msclkid: StringParam,
    utm_medium: StringParam,
    utm_source: StringParam,
    utm_campaign: StringParam,
    customerInfo: CustomerInfoParam,
  });

  const [locationsQueryData, getLocations, { loading, error: locQueryError }] = useLocationsQuery();
  const [eligiblePackages, getEligiblePackages, { error: eligiblePackagesError, loading: eligiblePackagesLoading }] =
    useEligiblePackageQuery();
  const limits = {
    max: truck ? 5 : 3,
    min: truck ? 2 : 1,
  };
  const [selected, setSelected] = useState();
  const [numberOfLocations, setNumberOfLocations] = useState();
  const [hasError, setHasError] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [privacyInfoModalOpen, setPrivacyInfoModalOpen] = useState(false);
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { customerLead } = useMarketing();
  const router = useRouter();

  const [flowNumber] = useQueryParam('flow', NumberParam);

  const [partners] = usePartners();
  const {
    clickid,
    gclid,
    msclkid,
    utm_medium: utmMedium,
    utm_source: utmSource,
    utm_campaign: utmCampaign,
    utm_content: utmContent,
  } = params;

  const { affiliateAccountId } =
    (utmContent?.includes('partnerships') || utmContent?.includes('affiliate')) && utmSource && partners
      ? partners?.find(({ identifier }) => identifier === utmSource) || ''
      : '';

  COPY.LOCATIONS.subheading = truck
    ? 'Address information helps us create an accurate estimate.'
    : 'If you need help at multiple locations, be sure to add an additional address.';

  const filteredNonEmptyLocations = selected?.filter(Boolean);

  const isContinueDisabled =
    hasError ||
    (truck ? Boolean(filteredNonEmptyLocations?.length < 2) : Boolean(filteredNonEmptyLocations?.length < 1)) ||
    eligiblePackagesLoading;

  const canAddLocation = numberOfLocations !== limits.max;

  const assembly = Boolean(process.env.NEXT_PUBLIC_ASSEMBLY === 'true');

  const message = assembly
    ? 'Because you selected movers only - a truck and supplies are not included. Make sure you book your own truck and driver, and have any needed supplies ready for move day.'
    : 'Because you selected movers only - a truck is not included. Make sure you book your own truck and driver for move day.';

  const addLocationField = () => {
    setNumberOfLocations(numberOfLocations + 1);
  };

  // Replaces the new location at the given index.
  const selectAddress = (i, location) => {
    if (i === 0) {
      track('Location - Starting Address - Selection', {});
    } else if (i === numberOfLocations - 1) {
      track('Location - Destination Address - Selection', {});
    } else {
      track('Location - Additional Address - Selection', {});
    }
    if (!privacyAcknowledged && (location.address.region === 'CA' || location.address.region === 'IL')) {
      setPrivacyInfoModalOpen(true);
    }
    const locationsCopy = [...selected];
    locationsCopy[i] = location;
    setSelected(locationsCopy);
  };

  // Keeps the location field, but removes the location from the "selected" array.
  const removeAddress = i => {
    const locationsCopy = [...selected];
    locationsCopy.splice(i, 1);
    setSelected(locationsCopy);
  };

  // removes location at index and deletes the location input field.
  const removeLocation = i => {
    if (selected[i]) {
      const locationsCopy = [...selected];
      locationsCopy.splice(i, 1);
      setSelected(locationsCopy);
    }
    setNumberOfLocations(numberOfLocations - 1);
  };

  const onValid = () => {
    getLocations(filteredNonEmptyLocations);
  };

  const handleLocations = async () => {
    if (locationsQueryData.length) {
      const updatedLocations = locationsQueryData.map(qLoc => {
        if (locations?.length) {
          return locations.find(loc => qLoc.id === loc.id) || qLoc;
        }
        return qLoc;
      });
      const newGroup = updatedLocations.length === 1 ? 'MOVERS_ONLY' : 'MOVERS_WITH_TRUCK';

      await setGroup(newGroup);
      await setLocations(updatedLocations);

      getEligiblePackages(
        updatedLocations.map(loc => loc.id),
        newGroup,
      );
    }
  };

  // Set params to update NEW locations only
  useEffect(() => {
    handleLocations();
  }, [locationsQueryData]);

  useEffect(() => {
    const newSelected = locations ? [...locations.slice(0, limits.max)] : [];

    if (!selected && !locations) setSelected(newSelected);
    if (locations) setSelected(newSelected);

    setNumberOfLocations(newSelected?.length > limits.min ? newSelected?.length : limits.min);
  }, [locations]);

  // Submits content from lead capture form
  const submitLead = async () => {
    const isLD = eligiblePackages?.some(ep => ep.code.includes('LD'));
    let leadType = isLD ? 'Long Distance' : 'Outbound';
    let leadSource = 'Bellhop Site - Booking Flow';
    let leadRecordType = isLD ? 'Long Distance Move' : 'Potential Customer';

    if (flowNumber === MLC_AVAILABILITY_FLOW_INDEX) {
      leadType = 'MLC';
      leadSource = 'Bellhop Site - Out of Service Area';
      leadRecordType = 'Referral';
    }

    let originLeadObj = {};
    let destinationLeadObj = {};

    if (locations?.length) {
      const originLocation = locations?.length ? locations[0] : { id: null };
      const destinationLocation = locations?.length ? locations[locations?.length - 1] : { id: null };

      originLeadObj = originLocation.id
        ? {
            origin_street: originLocation.address.line1,
            origin_city: originLocation.address.city,
            origin_state: originLocation.address.region,
            origin_postal_code: originLocation.address.postalCode,
          }
        : {};

      destinationLeadObj = destinationLocation.id
        ? {
            destination_street: destinationLocation.address.line1,
            destination_city: destinationLocation.address.city,
            destination_state: destinationLocation.address.region,
            destination_postal_code: destinationLocation.address.postalCode,
          }
        : {};
    }

    const trackingObj = {
      ...(clickid && { click_id: clickid }),
      ...(gclid && { gclid }),
      ...(msclkid && { msclkid }),
      ...(utmMedium && { utm_medium: utmMedium }),
      ...(utmSource && { utm_source: utmSource }),
      ...(utmCampaign && { utm_campaign: utmCampaign }),
    };

    await createLead({
      ...customerLead,
      ...trackingObj,
      ...originLeadObj,
      ...destinationLeadObj,
      lead_type: leadType,
      lead_source: leadSource,
      lead_record_type: leadRecordType,
    });
  };

  const redirectToLeadFlow = async () => {
    // If market not supported by us or MLC, continue to Lead Flow
    // If feature flag is on - redirect to v2 flow
    if (process.env.NEXT_PUBLIC_ENABLE_BOOKING_V2_LEAD === 'true') {
      const data = {
        locations: locations.map(({ address, marketCode }) => ({
          address,
          marketCode,
        })),
        customer: customerLead,
        truck: params.truck,
        gbIdentity: mixpanel.get_distinct_id(),
        affiliateAccountId,
      };
      const marketingParams = preserveMarketingParams(window.location.search);
      data.lead = true;
      router.push({
        pathname: `http://${process.env.NEXT_PUBLIC_BOOKING_V2_SITE_DOMAIN}/v2/book/entry`,
        search: `info=${Buffer.from(JSON.stringify(data)).toString('base64')}${
          marketingParams ? `&${marketingParams}` : ''
        }`,
      });
      return;
    }
    await setFlow(LEAD_FLOW_INDEX);
    setIsReady(true);
  };

  const handleFlows = async () => {
    if (!eligiblePackagesLoading && eligiblePackages) {
      setParams({
        customerInfo: customerLead,
      });
      if (eligiblePackages?.length === 0 || locations?.every(loc => loc.marketCode === 'null')) {
        // If Labor only and all locations are outside of supported markets, check MLC for capacity
        // We are disabling the MLC Redirect for Home Depot and Redfin as well
        const blockPartnerMlcRedirect = ['homedepot', 'redfin'].includes(params.utm_source);
        if (truck === 0 && locations?.every(loc => loc.marketCode === 'null') && !blockPartnerMlcRedirect) {
          let mlcSupported = null;
          // Foreach location, ensure availability by MLC
          const mlcServiceAvailability = await Promise.all(
            locations.map(({ address }) => MLCAPIService.getServiceAvailability(address.postalCode)),
          );
          mlcSupported = mlcServiceAvailability.every(avail => avail.providesService === true);

          // If MLC supported, continue to MLC availability flow
          if (mlcSupported) {
            // SetFlow to 9 (MLC_AVAILABILITY_FLOW_INDEX) and setIsReady to true
            // ./date.js handles redirect logic if in flow 9 (MLC_AVAILABILITY_FLOW_INDEX)
            await setFlow(MLC_AVAILABILITY_FLOW_INDEX);
            setIsReady(true);
          } else {
            redirectToLeadFlow();
          }
        } else {
          redirectToLeadFlow();
        }
      } else if (eligiblePackages?.length) {
        // Submit Lead
        await submitLead();
        // Navigate to next flow step
        if (eligiblePackages.some(ep => ep.code.includes('LD'))) {
          await setFlow(LONG_DISTANCE_FLOW_INDEX);
          setIsReady(true);
        } else if (eligiblePackages.length === 1 && locations.length === 1) {
          await setFlow(LABOR_ONLY_FLOW_INDEX);
          setIsReady(true);
        } else if (eligiblePackages.length === 2 && locations.length > 1 && truck === 1) {
          await setFlow(LABOR_ONLY_MARKET_FLOW_INDEX);
          setIsReady(true);
        } else {
          await setFlow(MOVING_SERVICES_FLOW_INDEX);
          setIsReady(true);
        }
      }
    }
  };

  const onContinue = async () => {
    track('Locations - Continue', {});

    identify(customerLead);

    if (flowNumber === MLC_AVAILABILITY_FLOW_INDEX) {
      await submitLead();
    }
  };

  useEffect(() => {
    handleFlows();
  }, [eligiblePackages]);

  return (
    <>
      <GoogleMapsScript mounted={mounted} setMounted={setMounted} />
      <PrivacyModal
        active={privacyInfoModalOpen}
        onClose={() => {
          setPrivacyAcknowledged(true);
          setPrivacyInfoModalOpen(false);
          track('Privacy Policy CA/IL ', {});
        }}
      />
      <Layout
        {...COPY.LOCATIONS}
        icon={{
          svg: LocationsIllustration,
          alt: 'Locations Illustration',
        }}
        button={{
          isDisabled: isContinueDisabled,
          isLoading: loading,
          isReady,
          onClick: onContinue,
        }}
        form={{
          isForm: true,
          onValid,
          onInvalid: () => {},
        }}
        alert={{
          type: 'error',
          message: locQueryError?.message || eligiblePackagesError?.message,
        }}
        reservation={{
          truck,
          long_distance: Boolean(flow === 2),
        }}
      >
        <Box display="flex" flexDirection="column" maxWidth={500} margin="0 auto">
          <LeadForm variant="standard" />
          {mounted && (
            <LocationsInput
              numberOfLocations={numberOfLocations}
              limits={limits}
              selected={selected}
              selectAddress={selectAddress}
              removeLocation={removeLocation}
              removeAddress={removeAddress}
              setHasError={setHasError}
            />
          )}
          {canAddLocation ? (
            <AddLocation addLocationField={addLocationField} />
          ) : (
            <Box pl={2}>
              <Message messageType="info" message={`We can only make ${limits.max} stops in a single move.`} />
            </Box>
          )}
          {!truck && (
            <Box>
              <AlertMessageBox message={message} Icon={Info} />
            </Box>
          )}

          <Box mt={4}>
            <Typography variant="caption" color="textSecondary" align="justify">
              {TCPA_FINE_PRINT}
            </Typography>
          </Box>
        </Box>
      </Layout>
    </>
  );
};

export default apiClient(withAuth(Location));
